import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppModule, Plan, TenantPlan } from '../models';

export const MODULE_LABELS: Record<AppModule, string> = {
  Agenda: 'Agenda',
  PublicPage: 'Página pública de agendamento',
  BookingNotifications: 'Notificações de agendamento',
  WhatsAppReminders: 'Lembretes automáticos via WhatsApp',
  MultiProfessional: 'Múltiplos profissionais',
  Crm: 'CRM de clientes',
  Loyalty: 'Fidelidade e assinaturas',
  Campaigns: 'Campanhas e promoções',
  Financial: 'Financeiro e fluxo de caixa',
  PrioritySupport: 'Suporte prioritário',
};

/** Plano da loja logada e módulos liberados (plano + avulsos do super admin). */
@Injectable({ providedIn: 'root' })
export class PlanService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/plans`;
  private pending$: Observable<TenantPlan | null> | null = null;

  readonly current = signal<TenantPlan | null>(null);
  readonly modules = computed(() => new Set(this.current()?.modules ?? []));

  has(module: AppModule): boolean { return this.modules().has(module); }

  /** Carrega uma vez (ou de novo com force) e compartilha a requisição em andamento. */
  load(force = false): Observable<TenantPlan | null> {
    if (!force && this.current()) return of(this.current());
    if (!force && this.pending$) return this.pending$;
    this.pending$ = this.http.get<TenantPlan>(`${this.base}/current`).pipe(
      tap((p) => this.current.set(p)),
      catchError(() => of(null)),
      tap(() => (this.pending$ = null)),
      shareReplay(1),
    );
    return this.pending$;
  }

  listPlans(): Observable<Plan[]> { return this.http.get<Plan[]>(this.base); }

  clear(): void { this.current.set(null); this.pending$ = null; }

  hasModule$(module: AppModule): Observable<boolean> {
    return this.load().pipe(map((p) => !!p && p.modules.includes(module)));
  }
}
